import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { motion } from "framer-motion";

type Mode = "web" | "mobile";

interface ModeConfig {
  src: string;
  poster?: string;
  posterAspect: string;
  maxWidth: string;
  /** Frame background — matches each asset's baked-in backdrop so edges blend. */
  background: string;
  /** Corner radius of the frame; mobile matches the phone's own rounded corners. */
  radius: string;
}

const S3 = "https://s3.us-east-1.amazonaws.com/fownd.care/Home+Videos";

const MODES: Record<Mode, ModeConfig> = {
  web: {
    src: `${S3}/merged-web.mp4`,
    poster: "/notation/starting.png",
    posterAspect: "3438 / 1574",
    maxWidth: "80rem",
    background: "#1F3D3B",
    radius: "1.5rem",
  },
  mobile: {
    // Cropped to the phone + web-optimized; black backdrop matches the phone
    // bezel so the remaining edge/rounded corners blend seamlessly.
    // radius matches Adobe Express corner roundness (240.09 in 1802px space).
    src: "/notation/hipeval-mobile.mp4",
    posterAspect: "760 / 1558",
    maxWidth: "20rem",
    background: "#000000",
    radius: "43px",
  },
};

const FRAME_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const getInitialMode = (): Mode =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 767px)").matches
    ? "mobile"
    : "web";

export default function NotationHeroShowcase() {
  // On phone-sized screens the mobile capture is the natural default;
  // desktop defaults to web. Manual WEB | MOBILE picks override until reload.
  const [mode, setMode] = useState<Mode>(getInitialMode);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [videoAspect, setVideoAspect] = useState(MODES[getInitialMode()].posterAspect);
  const videoRef = useRef<HTMLVideoElement>(null);
  const replayTimeoutRef = useRef<number | null>(null);
  const userPickedRef = useRef(false);

  const config = MODES[mode];

  // Keep the default in sync with viewport until the user toggles WEB | MOBILE.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const applyDefault = () => {
      if (userPickedRef.current) return;
      const next: Mode = mq.matches ? "mobile" : "web";
      setMode((prev) => {
        if (prev === next) return prev;
        setIsPlaying(false);
        setIsVideoVisible(false);
        setVideoAspect(MODES[next].posterAspect);
        return next;
      });
    };
    applyDefault();
    mq.addEventListener("change", applyDefault);
    return () => mq.removeEventListener("change", applyDefault);
  }, []);

  useEffect(() => {
    if (!isPlaying || !videoRef.current) return;
    void videoRef.current.play();
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      if (replayTimeoutRef.current) {
        window.clearTimeout(replayTimeoutRef.current);
      }
    };
  }, []);

  const startVideo = () => {
    if (replayTimeoutRef.current) {
      window.clearTimeout(replayTimeoutRef.current);
    }

    setIsVideoVisible(false);

    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }

    setIsPlaying(true);
  };

  const replayFromPoster = () => {
    setIsVideoVisible(false);

    if (!videoRef.current) return;

    videoRef.current.pause();
    videoRef.current.currentTime = 0;

    replayTimeoutRef.current = window.setTimeout(() => {
      void videoRef.current?.play();
    }, 900);
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;

    userPickedRef.current = true;

    if (replayTimeoutRef.current) {
      window.clearTimeout(replayTimeoutRef.current);
    }

    setIsPlaying(false);
    setIsVideoVisible(false);
    setVideoAspect(MODES[next].posterAspect);
    setMode(next);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.8 }}
      className="relative w-full max-w-7xl mx-auto flex flex-col items-center group"
    >
      <div
        className="relative w-full mx-auto overflow-hidden border border-white/10 bg-[#1a3230]/40 shadow-[0_24px_80px_-20px_rgba(97,158,211,0.55),0_12px_40px_-12px_rgba(0,0,0,0.45)] group-hover:shadow-[0_32px_100px_-16px_rgba(97,158,211,0.65),0_16px_48px_-12px_rgba(0,0,0,0.5)] group-hover:border-[#619ed3]/30"
        style={{
          maxWidth: config.maxWidth,
          borderRadius: config.radius,
          transition: `max-width 950ms ${FRAME_EASE}, border-radius 950ms ${FRAME_EASE}, box-shadow 500ms ease, border-color 500ms ease`,
        }}
      >
        <div
          className="absolute -inset-1 bg-gradient-to-br from-[#619ed3]/20 via-transparent to-[#486633]/15 opacity-60 blur-xl pointer-events-none group-hover:opacity-90 transition-opacity duration-500"
          style={{ borderRadius: config.radius }}
        />

        <div
          className="relative w-full"
          style={{
            backgroundColor: config.background,
            aspectRatio: isPlaying ? videoAspect : config.posterAspect,
            transition: `aspect-ratio 950ms ${FRAME_EASE}, background-color 500ms ease`,
          }}
        >
          <video
            key={mode}
            ref={videoRef}
            className="w-full h-full object-cover block"
            autoPlay={isPlaying}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(event) => {
              const { videoWidth, videoHeight } = event.currentTarget;
              if (videoWidth && videoHeight) {
                setVideoAspect(`${videoWidth} / ${videoHeight}`);
              }
            }}
            onPlaying={() => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => setIsVideoVisible(true));
              });
            }}
            onEnded={replayFromPoster}
            aria-label={`Notation ${mode} workflow preview`}
          >
            <source src={config.src} type="video/mp4" />
          </video>

          <div
            className={`absolute inset-0 transition-opacity duration-500 ease-out ${
              isPlaying && isVideoVisible ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          >
            {config.poster ? (
              <img
                src={config.poster}
                alt="Notation clinical documentation interface"
                className="w-full h-full object-contain block"
              />
            ) : null}
            {!isPlaying && (
              <button
                type="button"
                onClick={startVideo}
                aria-label="Play notation workflow preview"
                className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/5 transition-colors duration-300"
              >
                <span className="flex items-center justify-center w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white shadow-[0_8px_32px_rgba(97,158,211,0.35)] transition-all duration-300 group-hover:scale-110 group-hover:bg-white/25 group-hover:shadow-[0_12px_40px_rgba(97,158,211,0.5)]">
                  <Play className="w-7 h-7 md:w-8 md:h-8 ml-1 fill-white" />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* WEB | MOBILE toggle */}
      <div className="mt-5 flex items-center justify-center gap-4 font-fjalla text-lg sm:text-xl md:text-2xl select-none tracking-wide">
        <button
          type="button"
          onClick={() => switchMode("web")}
          className={`transition-colors duration-200 hover:text-[#619ed3] ${
            mode === "web" ? "text-[#619ed3] font-semibold" : "text-gray-400"
          }`}
        >
          WEB
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={() => switchMode("mobile")}
          className={`transition-colors duration-200 hover:text-[#619ed3] ${
            mode === "mobile" ? "text-[#619ed3] font-semibold" : "text-gray-400"
          }`}
        >
          MOBILE
        </button>
      </div>
    </motion.div>
  );
}
